import { Loader2, Paperclip } from "lucide-react";
import { type ChangeEvent, useId, useState } from "react";
import { cn } from "../cn";

/**
 * The document MIME types the backend accepts, plus `.eml` e-mail files
 * (which browsers do not reliably match by MIME type, listed by extension).
 *
 * ponytail: inlined copy of admin-v2's `media/mimeTypes` `MEDIA_FILE_ACCEPT`;
 * a package cannot depend on an app's feature folder. If the backend's
 * `AllowedDocumentMedia::ALLOWED_MIME_TYPES` changes, update both.
 */
const DEFAULT_FILE_ACCEPT =
  "application/pdf,image/jpeg,image/png,image/webp,.eml";

export interface FileInputProps {
  readonly label: string;
  /** Called with the chosen file. The component never uploads by itself. */
  readonly onFileSelected: (file: File) => void;
  /** Comma-separated `accept` list; defaults to {@link DEFAULT_FILE_ACCEPT}. */
  readonly accept?: string;
  /** Optional hint (e.g. "Max. 10 MB") rendered beneath the control. */
  readonly maxSizeHint?: string;
  readonly disabled?: boolean;
  /** Reflects an in-flight upload: disables the control and shows a spinner. */
  readonly loading?: boolean;
  readonly id?: string;
  readonly className?: string;
  readonly "aria-describedby"?: string;
  readonly "aria-invalid"?: boolean;
}

/**
 * Accessible, presentational file picker. Owns only the native
 * `<input type="file">`, its label association and a selected-filename display;
 * it delegates the actual upload to the caller via {@link FileInputProps.onFileSelected}.
 */
export function FileInput({
  label,
  onFileSelected,
  accept = DEFAULT_FILE_ACCEPT,
  maxSizeHint,
  disabled = false,
  loading = false,
  id,
  className,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
}: FileInputProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const hintId = `${inputId}-hint`;
  const [fileName, setFileName] = useState<string | null>(null);
  const isDisabled = disabled || loading;

  function handleChange(event: ChangeEvent<HTMLInputElement>): void {
    const file = event.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    onFileSelected(file);
    // Reset so re-selecting the same file fires onChange again (retry after error).
    event.target.value = "";
  }

  const describedBy =
    [ariaDescribedBy, maxSizeHint ? hintId : undefined]
      .filter(Boolean)
      .join(" ") || undefined;

  return (
    <div className={cn("space-y-1.5", className)}>
      <label
        htmlFor={inputId}
        className="block text-sm font-medium text-foreground"
      >
        {label}
      </label>
      <input
        id={inputId}
        type="file"
        accept={accept}
        disabled={isDisabled}
        onChange={handleChange}
        aria-describedby={describedBy}
        aria-invalid={ariaInvalid}
        className="block w-full text-sm text-foreground file:mr-3 file:cursor-pointer file:rounded-lg file:border-0 file:bg-primary file:px-3 file:py-2 file:text-sm file:font-medium file:text-primary-foreground hover:file:bg-primary/90 disabled:pointer-events-none disabled:opacity-50"
      />
      {(loading || fileName) && (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          {loading ? (
            <>
              <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
              <span>Bestand uploaden…</span>
            </>
          ) : (
            <>
              <Paperclip className="size-3.5" aria-hidden="true" />
              <span>{fileName}</span>
            </>
          )}
        </p>
      )}
      {maxSizeHint && (
        <p id={hintId} className="text-xs text-muted-foreground">
          {maxSizeHint}
        </p>
      )}
    </div>
  );
}
